import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Code2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";

export default function ItsmPayloadDialog({ ticketId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    endpoints.servicenowPayload(ticketId).then((d) => { setData(d); setLoading(false); });
  };

  useEffect(() => {
    if (open && !data && ticketId) load();
  }, [open, ticketId, data]);

  const handleOpenChange = (o) => {
    onOpenChange(o);
    if (o && !data) load();
  };

  const copy = async (obj) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
      toast.success("Payload copied to clipboard");
    } catch {
      toast.error("Could not copy — please select and copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="itsm-payload-dialog" className="max-w-3xl bg-white p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b border-slate-200">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Code2 size={12} className="text-[#0033A0]" /> Integration Hub · REST API
          </div>
          <DialogTitle className="font-display text-2xl text-slate-900">ITSM Integration Payload</DialogTitle>
          <DialogDescription>
            Exactly what Flexspace will POST to your ITSM system for this ticket. Bring this to your ServiceNow / BigFix onboarding call.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading || !data ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <Tabs defaultValue="servicenow" data-testid="itsm-payload-tabs">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="servicenow" data-testid="tab-servicenow" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">ServiceNow</TabsTrigger>
                <TabsTrigger value="bigfix" data-testid="tab-bigfix" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">BigFix Service Mgmt</TabsTrigger>
              </TabsList>

              {["servicenow", "bigfix"].map((k) => (
                <TabsContent key={k} value={k} className="mt-4 space-y-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Endpoint</div>
                    <div className="mt-1 font-mono text-xs bg-slate-900 text-emerald-300 rounded-md px-3 py-2 break-all">{data.integrations[k].endpoint}</div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Headers</div>
                    </div>
                    <pre className="mt-1 font-mono text-xs bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto">
{JSON.stringify(data.integrations[k].headers, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Body (JSON)</div>
                      <Button size="sm" variant="outline" onClick={() => copy(data.integrations[k].body)} data-testid={`copy-payload-${k}`}>
                        <Copy size={12} className="mr-1.5" /> Copy JSON
                      </Button>
                    </div>
                    <pre className="mt-1 font-mono text-xs bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto max-h-72">
{JSON.stringify(data.integrations[k].body, null, 2)}
                    </pre>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
